import { useCallbackRef, useConstant, useIntersectionObserver } from '@cian/react-utils';

import { type FC, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { clearViewTimeouts, type TViewShowTimeouts } from 'shared/utils/postViewAnalytics/clearViewTimeouts';
import { type FinalAdfoxAnalyticsPayload, type MiddleAdfoxAnalyticsPayload } from 'shared/types/analytics';
import { type IAdfoxBannerOnErrorError, type TAdfoxBannerOnLoadData, TAdfoxTemplatesParams } from 'shared/types/adfox';
import { AdfoxBanner } from 'shared/components/AdfoxBanner';
import { EAdfoxAnalyticsPayloadType } from 'shared/enums/adfoxAnalyticsPayloadType';
import { EBannerSource } from 'shared/enums/bannerSource';
import { getJsonFields } from 'shared/utils/postViewAnalytics/getJsonFields';
import { sendTeaserWebAdfoxEventFactory } from 'shared/utils/postViewAnalytics/sendTeaserWebEvent';

import { useAdfoxBannerContext } from '../AdfoxBannerContext';

export const AdBannerWithPostViewAnalytics: FC = () => {
  const { analyticsData, params, eventEmitter, containerId } = useAdfoxBannerContext();

  const timeouts = useConstant<TViewShowTimeouts>(() => ({
    view1: null,
    view2: null,
  }));

  const iframeInnerElementRef = useRef<HTMLBodyElement | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  const adfoxBannerOnLoadDataRef = useRef<TAdfoxBannerOnLoadData | null>(null);
  const parsedDecodedParamsRef = useRef<TAdfoxTemplatesParams | null>(null);

  const middleAdfoxAnalyticsPayload: MiddleAdfoxAnalyticsPayload = useConstant(() => ({
    ...analyticsData,
    bannerSource: EBannerSource.Adfox,
    p1: params.p1,
    p2: params.p2,
    traceId: uuidv4(),
    type: EAdfoxAnalyticsPayloadType.WithoutAdfoxData,
  }));

  const sendTeaserWebEventRef = useCallbackRef(() => sendTeaserWebAdfoxEventFactory(middleAdfoxAnalyticsPayload));

  /** 50% видимости: show1 через 1с непрерывной видимости, show2 ещё через 1с. */
  const { subscribeState: subscribeState50PercentVisibility } = useIntersectionObserver({
    elementRef,
    initSubscribeAvailable: false,
    observerOptions: { threshold: 0.5 },
    onInViewport({ subscribeState: { setSubscribeUnavailable } }) {
      timeouts.view1 = setTimeout(() => {
        timeouts.view2 = setTimeout(() => {
          sendTeaserWebEventRef.current('show2');
        }, 1000);

        sendTeaserWebEventRef.current('show1');
      }, 1000);

      setSubscribeUnavailable();
    },
    onOutViewport({ observer, subscribeState: { setSubscribeUnavailable }, intersectionsCount }) {
      if (intersectionsCount.inView) {
        observer.disconnect();
        setSubscribeUnavailable();

        clearViewTimeouts(timeouts);
      }
    },
  });

  /** 1% видимости: одноразовый триггер show_1px и включение отслеживания 50% порога. */
  const { subscribeState: subscribeState1PercentVisibility } = useIntersectionObserver({
    elementRef,
    initSubscribeAvailable: false,
    observerOptions: { threshold: 0.01 },
    onInViewport({ observer, subscribeState: { setSubscribeUnavailable } }) {
      eventEmitter.emit({
        containerId,
        data: adfoxBannerOnLoadDataRef.current as TAdfoxBannerOnLoadData,
        event: 'show_1px',
        parsedDecodedParams: parsedDecodedParamsRef.current,
      });

      sendTeaserWebEventRef.current('show_1px');

      subscribeState50PercentVisibility.setSubscribeAvailable();

      observer.disconnect();
      setSubscribeUnavailable();
    },
  });

  /** Обработчик события click из общего eventEmitter: здесь происходит отправка аналитики клика. */
  const handleClick = useCallback<VoidFunction>(() => {
    sendTeaserWebEventRef.current('click');
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Источник клика для HTML-баннеров в iframe.
   * Нельзя отправлять аналитику напрямую отсюда, чтобы не дублировать каналы клика:
   * клик всегда нормализуется через единый поток eventEmitter -> handleClick.
   */
  const handleClickTrigger = useCallback((event: MouseEvent) => {
    eventEmitter.emit({ containerId, event: 'click', eventData: event });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Подписка на событие загрузки баннера */
  const handleLoad = useCallback<
    (data: TAdfoxBannerOnLoadData, parsedDecodedParams: TAdfoxTemplatesParams | null) => void
  >((data, parsedDecodedParams) => {
    const { bundleParams, bundleName } = data;

    adfoxBannerOnLoadDataRef.current = data;
    parsedDecodedParamsRef.current = parsedDecodedParams;

    const finalAdfoxAnalyticsPayload: FinalAdfoxAnalyticsPayload = {
      ...middleAdfoxAnalyticsPayload,
      bundleName,
      campaignId: bundleParams.campaignId,
      id: Number(bundleParams.bannerId),
      jsonFields: getJsonFields(parsedDecodedParams ?? {}),
      type: EAdfoxAnalyticsPayloadType.WithAdfoxData,
    };

    sendTeaserWebEventRef.current = sendTeaserWebAdfoxEventFactory(finalAdfoxAnalyticsPayload);

    sendTeaserWebEventRef.current('load');
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Подписка на событие заглушки баннера */
  const handleStub = useCallback(() => {
    sendTeaserWebEventRef.current('stub');
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Подписка на событие ошибки баннера */
  const handleError = useCallback<(error: IAdfoxBannerOnErrorError) => void>(error => {
    sendTeaserWebEventRef.current = sendTeaserWebAdfoxEventFactory({
      ...middleAdfoxAnalyticsPayload,
      errorMessage: error.message,
      errorType: error.type,
      type: EAdfoxAnalyticsPayloadType.WithAdfoxErrorData,
    });

    sendTeaserWebEventRef.current('error');
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Подписка на событие рендера баннера. */
  const handleRender = useCallback<VoidFunction>(() => {
    /**
     * Для HTML-баннеров, вложенных в iframe, клики не всплывают наружу и не отлавливаются средствами React.
     * Поэтому если внутри контейнера есть iframe, вешаем обработчик клика на его body.
     */
    subscribeState1PercentVisibility.setSubscribeAvailable();

    const iframeElement = document.querySelector<HTMLIFrameElement>(`#${containerId} iframe`);

    if (iframeElement) {
      const iframeDocument = iframeElement.contentDocument || iframeElement.contentWindow?.document;

      const iframeBodyElement = iframeDocument?.querySelector<HTMLBodyElement>('body') ?? null;

      iframeInnerElementRef.current = iframeBodyElement;

      if (iframeInnerElementRef.current) {
        iframeInnerElementRef.current.addEventListener('click', handleClickTrigger);
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Подписка на событие закрытия баннера */
  const handleClose = useCallback(() => {
    sendTeaserWebEventRef.current('close');
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Аналитика подписывается на жизненный цикл баннера через общий eventEmitter,
   * чтобы все источники событий (DOM, adfox callbacks, iframe) сходились в одном канале.
   */
  useEffect(() => {
    const unsubscribes = [
      eventEmitter.subscribe('load', data => handleLoad(data.data, data.parsedDecodedParams)),
      eventEmitter.subscribe('stub', handleStub),
      eventEmitter.subscribe('error', data => handleError(data.error)),
      eventEmitter.subscribe('render', handleRender),
      eventEmitter.subscribe('close', handleClose),
      eventEmitter.subscribe('click', handleClick),
    ];

    return (): void => {
      if (iframeInnerElementRef.current) {
        iframeInnerElementRef.current.removeEventListener('click', handleClickTrigger);
      }

      clearViewTimeouts(timeouts);

      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AdfoxBanner ref={elementRef} />;
};
